import type { FeatureKeyT, LimitKeyT } from "@malfak/contracts";
import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useFeature, useLimit } from "./use-entitlements";

/**
 * Feature gate (ALP-81). `mode="hide"` renders nothing when the plan lacks the feature; `mode="disable"` (default)
 * renders `fallback` or a small locked note with the localized reason and an "Upgrade" placeholder.
 * While entitlements load (or fail) children render: the API enforces anyway.
 * STABLE API: `<Gate feature mode? fallback?>`, `<LockedNote feature?>`, `useGateReason()`.
 */
export function Gate({ feature, mode = "disable", fallback, children }: { feature: FeatureKeyT; mode?: "hide" | "disable"; fallback?: ReactNode; children: ReactNode }) {
  const { enabled } = useFeature(feature);
  if (enabled) return <>{children}</>;
  if (mode === "hide") return null;
  return <>{fallback ?? <LockedNote feature={feature} />}</>;
}

/** "Not included in your plan · Upgrade" (or "You reached your plan limit" with `limit`). */
export function LockedNote({ feature, limit, className }: { feature?: FeatureKeyT; limit?: LimitKeyT; className?: string }) {
  const { t } = useT();
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-1.5 text-xs text-ink-3", className)} data-locked-feature={feature} data-locked-limit={limit} data-testid="plan-locked">
      <Lock aria-hidden className="size-3.5" />
      <span>{limit ? t("plans.gate.limitReached") : t("plans.gate.notInPlan")}</span>
      <Link to="/settings/plan" className="font-medium text-accent-ink underline-offset-2 hover:underline">
        {t("plans.gate.upgrade")}
      </Link>
    </span>
  );
}

/**
 * Why a create/act button is unavailable: the feature isn't in the plan, or the gauge limit (rules, dashboards,
 * accounts…) is full. null = allowed. Use it to disable a button and show `<LockedNote>` next to it.
 */
export function useGateReason(feature?: FeatureKeyT, limit?: LimitKeyT): { feature?: FeatureKeyT; limit?: LimitKeyT } | null {
  const f = useFeature(feature ?? "copilot");
  const l = useLimit(limit ?? "seats");
  if (feature && !f.enabled) return { feature };
  if (limit && l && l.limit !== null && l.used !== null && l.used >= l.limit) return { limit };
  return null;
}
